/**
 * PaymentMethodFormModal Component
 *
 * 결제수단 추가/수정을 위한 모달 폼
 *
 * 책임:
 * - 결제수단 입력 폼 제공
 * - 유효성 검증
 * - 생성/수정 구분 처리
 */

import { useState, useEffect } from 'react'
import { Modal } from '@/shared/components/ui/Modal'
import { useToast } from '@/shared/components/ui/Toast'
import type { PaymentMethod, PaymentMethodType } from '../types/PaymentMethod'
import { useUsers } from '@/domains/user/hooks/useUsers'

/**
 * PaymentMethodFormModal Props
 */
export interface PaymentMethodFormModalProps {
  /** 모달 표시 여부 */
  show: boolean
  /** 수정할 결제수단 (수정 모드) */
  editingPaymentMethod?: PaymentMethod | null
  /** 닫기 핸들러 */
  onClose: () => void
  /** 저장 핸들러 */
  onSave: (input: Omit<PaymentMethod, 'id'>) => Promise<void>
}

/**
 * 결제수단 폼 모달
 *
 * 추가/수정 모드를 지원하며, 이름, 유형, 소유자, 메모 입력 가능
 */
export function PaymentMethodFormModal({
  show,
  editingPaymentMethod,
  onClose,
  onSave,
}: PaymentMethodFormModalProps) {
  const toast = useToast()
  // 사용자 목록 조회
  const { users, loading: usersLoading, error: usersError } = useUsers()

  const [name, setName] = useState('')
  const [type, setType] = useState<PaymentMethodType>('cash')
  const [ownerId, setOwnerId] = useState('') // 초기값 빈 문자열
  const [memo, setMemo] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [saving, setSaving] = useState(false)

  /**
   * 수정 모드일 때 폼 초기화
   */
  useEffect(() => {
    if (editingPaymentMethod) {
      setName(editingPaymentMethod.name)
      setType(editingPaymentMethod.type)
      setOwnerId(editingPaymentMethod.ownerId)
      setMemo(editingPaymentMethod.memo)
      setIsActive(editingPaymentMethod.isActive)
    } else {
      // 추가 모드일 때 폼 초기화
      setName('')
      setType('cash')
      setOwnerId(users[0]?.id || '') // 첫 번째 사용자를 기본값으로
      setMemo('')
      setIsActive(true)
    }
  }, [editingPaymentMethod, show, users])

  /**
   * 저장 핸들러
   */
  const handleSave = async () => {
    // 유효성 검증
    if (!name.trim()) {
      toast.warning('결제수단 이름을 입력해주세요')
      return
    }

    if (!ownerId) {
      toast.warning('소유자를 선택해주세요')
      return
    }

    try {
      setSaving(true)
      await onSave({
        name: name.trim(),
        type,
        ownerId,
        memo: memo.trim(),
        isActive,
      })
    } catch (err) {
      // 에러는 부모 컴포넌트에서 처리
      console.error('저장 실패:', err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      isOpen={show}
      onClose={onClose}
      title={editingPaymentMethod ? '결제수단 수정' : '결제수단 추가'}
      size="sm"
    >
      {/* 폼 */}
      <div className="space-y-4">
          {/* 이름 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              이름 *
            </label>
            <input
              type="text"
              inputMode="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="예: 신한 체크카드"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
            />
          </div>

          {/* 유형 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              유형 *
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as PaymentMethodType)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
            >
              <option value="cash">현금</option>
              <option value="debit_card">체크카드</option>
              <option value="credit_card">신용카드</option>
              <option value="simple_pay">간편결제</option>
              <option value="bank_transfer">계좌이체</option>
            </select>
          </div>

          {/* 소유자 선택 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              소유자 *
            </label>
            {usersLoading ? (
              <div className="text-gray-500 dark:text-gray-400 text-sm">사용자 목록 로딩 중...</div>
            ) : usersError ? (
              <div className="text-red-500 text-sm">사용자 목록을 불러올 수 없습니다</div>
            ) : users.length === 0 ? (
              <div className="text-yellow-600 text-sm">등록된 사용자가 없습니다. 먼저 사용자를 추가해주세요.</div>
            ) : (
              <select
                value={ownerId}
                onChange={(e) => setOwnerId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
              >
                <option value="" disabled>소유자 선택</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* 메모 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              메모
            </label>
            <textarea
              inputMode="text"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              placeholder="예: 생활비 전용"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
            />
          </div>

          {/* 활성 상태 */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isActive"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="w-4 h-4 text-primary-600 dark:text-primary-400 border-gray-300 rounded focus:ring-primary-500 dark:focus:ring-primary-400"
            />
            <label htmlFor="isActive" className="ml-2 text-sm text-gray-700 dark:text-gray-200">
              활성 상태
            </label>
          </div>
        </div>

        {/* 버튼 */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            disabled={saving}
            className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            취소
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-4 py-2 bg-primary-600 dark:bg-primary-500 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
    </Modal>
  )
}
